import { ReturnHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useHotkey } from '@modules/common/hooks';
import { ArrowBackRounded, OpenInNewRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

interface IntegrationDisplayNameConfirmationDialogContentProps {
  displayName: string;
  onConfirm: () => void;
}

export const IntegrationDisplayNameConfirmationDialogContent = ({
  displayName,
  onConfirm,
}: IntegrationDisplayNameConfirmationDialogContentProps) => {
  const { closeModal } = useMuiModalContext();

  const confirmAndIntegrate = () => {
    onConfirm();
    closeModal();
  };

  useHotkey({
    hotkey: 'enter',
    onHotkey: confirmAndIntegrate,
    isHotKeyDisabled: false,
    actionTitle: 'Confirm Integration Name',
  });

  const renderFeedbackCardIllustration = () => {
    return (
      <Stack
        spacing={2}
        bgcolor={(theme) => alpha(theme.palette.text.primary, 0.05)}
        border="1px solid"
        borderColor={(theme) => alpha(theme.palette.text.primary, 0.08)}
        p={2}
        borderRadius={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            width={22}
            height={22}
            borderRadius="50%"
            bgcolor={(theme) => alpha(theme.palette.text.primary, 0.1)}
          />
          <Typography variant="body1">{displayName}</Typography>
          <OpenInNewRounded
            fontSize="small"
            sx={{
              color: (theme) => alpha(theme.palette.text.primary, 0.2),
            }}
          />
        </Stack>
        <Stack
          p={3}
          spacing={2}
          bgcolor={(theme) => alpha(theme.palette.text.primary, 0.05)}
          borderRadius={2}
        >
          <Box
            height={12}
            width="100%"
            bgcolor={(theme) => alpha(theme.palette.text.primary, 0.1)}
            borderRadius={8}
          />
          <Box
            height={12}
            width="100%"
            bgcolor={(theme) => alpha(theme.palette.text.primary, 0.1)}
            borderRadius={8}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            height={20}
            width={130}
            bgcolor={(theme) => alpha(theme.palette.text.primary, 0.1)}
            borderRadius={8}
          />
          <Box
            height={20}
            width={130}
            bgcolor={(theme) => alpha(theme.palette.text.primary, 0.1)}
            borderRadius={8}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <DialogTitle>Confirm "{displayName}" as integration name?</DialogTitle>
      <Divider light />
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">
            Please ensure the name is easy for end-users to understand, and
            represents the true source of the feedback. Are you sure you want to
            proceed with "{displayName}"?
          </Typography>
          <Alert severity="warning">
            PS: An integration's name cannot be changed after confirming.
          </Alert>
          {renderFeedbackCardIllustration()}
        </Stack>
      </DialogContent>
      <Divider light />
      <DialogActions sx={{ my: 1, mx: 2 }}>
        <Button
          variant="outlined"
          onClick={() => closeModal()}
          startIcon={<ArrowBackRounded />}
        >
          Go Back
        </Button>
        <Button
          variant="contained"
          onClick={confirmAndIntegrate}
          endIcon={<ReturnHotKeyIcon />}
        >
          Confirm And Integrate
        </Button>
      </DialogActions>
    </>
  );
};
